import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ExternalLink, Search } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { AdminTooltip } from "@/components/admin/AdminTooltip";
import { Input } from "@/components/ui/input";
import { CATEGORIES } from "@/lib/categories";

export const Route = createFileRoute("/_authenticated/admin/sidor")({
  loader: async () => {
    const [{ GUIDES }, { CITIES }, { COMPARISONS }] = await Promise.all([
      import("@/lib/guides"),
      import("@/lib/orter-seo"),
      import("@/lib/jamforelser"),
    ]);
    return {
      guides: GUIDES.map((g) => ({ slug: g.slug, title: g.title, category: g.category })),
      cities: CITIES.map((c) => ({ slug: c.slug, name: c.name, lan: c.lan })),
      comparisons: COMPARISONS.map((c) => ({ slug: c.slug, name: c.name })),
    };
  },
  head: () => ({
    meta: [
      { title: "Alla sidor — Sellavo admin" },
      {
        name: "description",
        content: "Överblick över alla publika sidor på Sellavo och var de hör hemma.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SidorPage,
});

type PageRow = { path: string; label: string; help: string };
type PageGroup = { title: string; help: string; rows: PageRow[] };

/** Alla publika rutfiler – används för att hitta sidor som glömts bort här. */
const ROUTE_FILES = import.meta.glob("/src/routes/**/*.tsx", { eager: false });

const STATIC_LABELS: Record<string, { label: string; help: string }> = {
  "/": { label: "Startsidan", help: "Förstasidan med senaste annonserna och säljflödet." },
  "/annonser": { label: "Alla annonser", help: "Sökbar lista med filter, karta och kategorier." },
  "/skapa": { label: "Skapa annons", help: "Säljflödet där AI skriver text och föreslår pris." },
  "/videoskanning": {
    label: "Videoskanning",
    help: "Landningssida för filmskanningen som hittar prylar i en video.",
  },
  "/priser": { label: "Priser", help: "Jämförelse av Gratis, Plus, Pro och Expert." },
  "/varfor-kostar-det": { label: "Varför kostar det?", help: "Förklarar vad avgifterna går till." },
  "/sa-fungerar-det": { label: "Så fungerar det", help: "Steg för steg om hur sajten funkar." },
  "/tips": { label: "Tips", help: "Korta säljtips – bra ingång från sökmotorer." },
  "/support": { label: "Support", help: "Kontaktformuläret som skapar supportärenden." },
  "/om": { label: "Om Sellavo", help: "Berättelsen om sajten – används även av AI-sökmotorer." },
  "/regler": { label: "Annonsregler", help: "Vad som får och inte får säljas." },
  "/anvandarvillkor": { label: "Användarvillkor", help: "Juridiskt avtal mellan dig och användaren." },
  "/integritetspolicy": { label: "Integritetspolicy", help: "Hur personuppgifter hanteras (GDPR)." },
  "/cookiepolicy": { label: "Cookiepolicy", help: "Vilka kakor som används och varför." },
  "/guider": { label: "Guider (nav)", help: "Navsidan som länkar vidare till alla 18 guider." },
  "/kopa-och-salja": { label: "Orter (nav)", help: "Navsidan för alla ortssidor." },
  "/jamfor": { label: "Jämförelser (nav)", help: "Navsidan för jämförelser mot andra tjänster." },
  "/glomt-losenord": { label: "Glömt lösenord", help: "Ej indexerad – del av inloggningsflödet." },
  "/reset-password": { label: "Nytt lösenord", help: "Ej indexerad – nås via mejllänk." },
  "/bekraftad": { label: "Bekräftad e-post", help: "Ej indexerad – landar hit efter verifiering." },
};

function staticPaths(): string[] {
  const paths = new Set<string>();
  for (const file of Object.keys(ROUTE_FILES)) {
    let rel = file.replace(/^\/?src\/routes\//, "").replace(/\.tsx$/, "");
    if (rel.startsWith("__root") || rel.startsWith("_") || rel.startsWith("api/")) continue;
    if (rel.includes("$") || rel.includes("[")) continue;
    rel = rel.replace(/\./g, "/");
    if (rel === "index") {
      paths.add("/");
      continue;
    }
    rel = rel.replace(/\/index$/, "");
    paths.add(`/${rel}`);
  }
  return [...paths].sort();
}

function SidorPage() {
  const { guides, cities, comparisons } = Route.useLoaderData();
  const [q, setQ] = useState("");

  const groups = useMemo<PageGroup[]>(() => {
    const statics: PageRow[] = staticPaths().map((path) => ({
      path,
      label: STATIC_LABELS[path]?.label ?? path,
      help: STATIC_LABELS[path]?.help ?? "Publik sida utan egen beskrivning ännu.",
    }));

    return [
      {
        title: "Sajtens sidor",
        help: "Fasta sidor som ligger i menyn och i sidfoten. Alla utom inloggningsflödet ligger i sitemapen.",
        rows: statics,
      },
      {
        title: "Guider",
        help: "Långa SEO-artiklar (700–900 ord) med FAQ och schema-märkning. De ska dra in sökningar som ”sälja begagnat på nätet”.",
        rows: guides.map((g) => ({
          path: `/guider/${g.slug}`,
          label: g.title,
          help: `Guide i kategorin ${g.category}. Innehåller kort svar, steg-för-steg och FAQ.`,
        })),
      },
      {
        title: "Kategorisidor",
        help: "En landningssida per kategori, t.ex. ”Sälja elektronik på nätet”, med egen text, prisguide och färska annonser.",
        rows: CATEGORIES.map((c) => ({
          path: `/salja/${c.slug}`,
          label: `Sälja ${c.label.toLowerCase()}`,
          help: `Landningssida för kategorin ${c.label} med FAQ och länkar till relaterade guider.`,
        })),
      },
      {
        title: "Ortssidor",
        help: "En sida per stad, t.ex. ”Köpa och sälja i Göteborg”. Fångar lokala sökningar.",
        rows: cities.map((c) => ({
          path: `/kopa-och-salja/${c.slug}`,
          label: `Köpa och sälja i ${c.name}`,
          help: `Lokal sida för ${c.name} (${c.lan}) med lokal text och FAQ.`,
        })),
      },
      {
        title: "Jämförelser",
        help: "Sidor som jämför Sellavo mot andra tjänster. Fångar sökningar som ”Blocket alternativ”.",
        rows: comparisons.map((c) => ({
          path: `/jamfor/${c.slug}`,
          label: `Sellavo vs ${c.name}`,
          help: `Jämförelsetabell och FAQ mot ${c.name}.`,
        })),
      },
    ];
  }, [guides, cities, comparisons]);

  const needle = q.trim().toLowerCase();
  const filtered = groups
    .map((g) => ({
      ...g,
      rows: needle
        ? g.rows.filter(
            (r) => r.label.toLowerCase().includes(needle) || r.path.toLowerCase().includes(needle),
          )
        : g.rows,
    }))
    .filter((g) => g.rows.length > 0);

  const total = groups.reduce((sum, g) => sum + g.rows.length, 0);

  return (
    <AdminShell title="Alla sidor">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-2xl text-sm text-foreground/75">
            Här ligger varje publik adress på sajten – {total} stycken. Håll muspekaren över en rad
            för att se vad sidan är till för. Alla utom inloggningsflödet ligger i{" "}
            <a
              href="/sitemap.xml"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-primary hover:underline"
            >
              sitemap.xml
            </a>
            .
          </p>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Sök sida"
              className="h-10 w-60 pl-9"
            />
          </div>
        </div>

        {filtered.map((group) => (
          <section key={group.title} className="rounded-xl border border-border bg-card p-4">
            <div className="flex flex-wrap items-center gap-2">
              <AdminTooltip text={group.help}>
                <h2 className="font-heading text-lg font-semibold">{group.title}</h2>
              </AdminTooltip>
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {group.rows.length}
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{group.help}</p>
            <ul className="mt-3 grid gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
              {group.rows.map((row) => (
                <li key={row.path}>
                  <AdminTooltip text={row.help}>
                    <a
                      href={row.path}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between gap-2 rounded-md border border-border/70 px-3 py-2 text-sm transition-colors hover:border-primary hover:bg-accent"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{row.label}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {row.path}
                        </span>
                      </span>
                      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    </a>
                  </AdminTooltip>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {!filtered.length && (
          <p className="text-sm text-muted-foreground">Ingen sida matchar ”{q}”.</p>
        )}

        <p className="text-sm text-muted-foreground">
          Vill du se hur sidorna presterar i Google?{" "}
          <Link to="/admin/statistik" className="font-medium text-primary hover:underline">
            Statistiken
          </Link>{" "}
          visar besök, källor och sökord.
        </p>
      </div>
    </AdminShell>
  );
}
